import { Calendar } from '@design-system/Calendar';
import { useEffect, useState } from 'react';
import { Button } from '@design-system/Button';
import {
  useGetIntroQuestionnaires,
  useIntroQuestionnaireMutation,
  useGetJourneyState
} from '@viome/viome-base/src/usecase/Home/HomeQuery';
import dayjs from 'dayjs';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { cn } from '@utils/styles';
import { LoadingPage } from '@screens/LoadingPage';
import { ReadOnlyLockIcon } from '@/components/icons/ReadOnlyLockIcon';
import { Loader } from '@design-system/Loader';

export const CollectSamplePage = () => {
  const {
    data: journeyStateData,
    isLoading: isJourneyStateLoading,
    isError: isJourneyStateError
  } = useGetJourneyState();
  const { data: introQuestionnaireData, isLoading } = useGetIntroQuestionnaires();
  const {isPending: answerAPILoading, mutate: submitAnswer} =
    useIntroQuestionnaireMutation();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionnarieData, setQuestionnarieData] = useState<any>(null);
  const [date, setDate] = useState<Date | undefined>(undefined);

  const { questionnaries = [], kitId } = introQuestionnaireData || {};

  useEffect(() => {
    if (questionnaries.length > 0) {
      const currentQuestion = questionnaries[currentIndex];
      setQuestionnarieData(currentQuestion);
      const currentAnswer = currentQuestion?.answer;
      if (currentAnswer) {
        setDate(new Date(dayjs(currentAnswer).toDate()));
      } else {
        setDate(undefined);
      }
    }
  }, [questionnaries, currentIndex]);

  const progressValue = questionnaries.length > 0 ? ((currentIndex + 1) / questionnaries.length) * 100 : 0;
  const currentIndexAtLastPage = currentIndex === questionnaries.length - 1;
  const isReadOnly = journeyStateData?.is_questionaire_processed || false;

  const onClickNext = (skipQuestion: boolean) => {
    if (answerAPILoading || skipQuestion) {
      if (skipQuestion && currentIndexAtLastPage) {
        navigate(-1);
      } else if (skipQuestion && !currentIndexAtLastPage) {
        setCurrentIndex(currentIndex + 1);
      }
      return;
    }
    if (isReadOnly) {
      if (currentIndexAtLastPage) {
        navigate(-1);
        return;
      }
      setCurrentIndex(currentIndex + 1);
      return;
    }
    if (currentIndex < (questionnaries.length - 1) || currentIndexAtLastPage) {
      submitAnswer(
        {
          answers: [
            {
              questionId: questionnarieData.question_id,
              answer: dayjs(date).format('YYYY-MM-DD'),
            },
          ],
          kitId: kitId,
          testId: questionnarieData.test_id,
        },
        {
          onSuccess: () => {
            if (currentIndexAtLastPage) {
              navigate(-1);
            } else {
              setCurrentIndex(currentIndex + 1);
            }
          },
        },
      );
    }
  };

  const onClickCollectLater = () => {
    setDate(undefined);
    onClickNext(true);
  }

  const onChangeDates = (newDate: Date | undefined) => {
    setDate(newDate || undefined);
  };

  const currentQuestionDateFromAPI = questionnarieData?.answer && dayjs(questionnarieData?.answer).format('MM/DD/YYYY');
  const changedQuestionDate = dayjs(date).format('MM/DD/YYYY')

  const updateOrNextButtonText = (currentQuestionDateFromAPI !== changedQuestionDate) ? 'Update' : 'Next';

  const onClickBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      navigate(-1);
    }
  };

  if (isJourneyStateLoading || isLoading) {
    return <LoadingPage />
  }

  if (isJourneyStateError || !introQuestionnaireData) {
    return <ErrorLoadingPage />
  }

  {/* Replace the below URL with S3 */}
  return (
    <div
      className="h-full flex flex-col"
      style={{
        backgroundImage: `url("https://res.cloudinary.com/dttltpafi/image/upload/v1736320353/image_xn8x6n.png")`,
        backgroundSize: 'cover',
      }}
    >
      <div className="p-6 space-y-4 page-container">
        <div className="flex items-center justify-between">
          <Button
            variant='ghost'
            className="mt-6 flex items-center gap-2 text-secondary"
            onClick={onClickBack}
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </Button>
        </div>

        <div className="w-full h-1 rounded-full overflow-hidden bg-stroke-secondary-subtle">
          <div
            className="h-full bg-background-success transition-all duration-300 ease-in-out"
            style={{ width: `${progressValue}%` }}
          />
        </div>
      </div>

      <div className="p-6 space-y-4 page-container">
        <div
          className={cn(
            "typography-d2 flex items-center my-12",
            !isReadOnly && 'justify-center'
          )}
        >
          {questionnarieData?.question_title}
        </div>
        {isReadOnly && (
          <div className='flex'>
            <ReadOnlyLockIcon />
            <div className='font-sans typography-body text-secondary ml-2'>
              Read only
            </div>
          </div>
        )}
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={date}
          onSelect={onChangeDates}
          fromDate={questionnarieData?.minDate}
          toDate={questionnarieData?.maxDate}
          month={date}
          disabled={isReadOnly}
        />
      </div>

      <div className={cn(
        'flex items-center p-6 page-container',
        isReadOnly ? 'justify-end' : 'justify-between'
      )}>
        {!isReadOnly && (
          <Button
            variant="ghost"
            className="text-secondary"
            disabled={answerAPILoading}
            onClick={onClickCollectLater}
          >
            I will collect later
          </Button>
        )}
        {answerAPILoading ? (
          // inline styles to override the existing styles and place the loader at required place
          <Loader style={{ inset: 'unset !important', position: 'static' }} />
        ) : (
          <Button
            variant="default"
            className="px-10 py-3"
            onClick={() => onClickNext(false)}
            disabled={answerAPILoading}
          >
            {currentIndexAtLastPage ? 'Done' : updateOrNextButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};
